import { FormArray, Validators } from "@angular/forms";

import ActionTypesEnum from "@shared/enums/action-constant.enum";
import { commonLineChartUtil } from "@shared/utils/common-line-chart.util";
import { createFormControl } from "@shared/utils/form.util";
import colorMap from "@shared/variables/chart-color.variable";

import { EChartOption } from "echarts";
import { Map } from "immutable";

export const getModalContent = (steps, modal, teamId, teamService, customEventService, scorecardId?) => {
  if (steps && steps.length) {
    steps.forEach((step, index) => {
      if (step.data && step.data.length === 1) {
        modal.steps[index].loading = true;
        getScorecardStep({
          stepIndex: index,
          key: step.data[0].key,
          modalContent: modal,
          teamId,
          teamService,
          customEventService,
          scorecardId
        });
      } else {
        modal.steps[index].loading = false;
      }
    });
  }
};

export const getScorecardStep = ({ stepIndex, modalContent, key, teamId, teamService, customEventService, scorecardId }: {
  stepIndex: number,
  modalContent: any,
  key: string,
  teamId: string,
  teamService: any,
  customEventService: any,
  scorecardId?: string
}): void => {
  modalContent.steps[stepIndex].loading = true;
  modalContent.steps[stepIndex].errorTitle = undefined;
  modalContent.steps[stepIndex].errorMessage = undefined;

  teamService.initializeScorecardTabDetail(teamId, key, scorecardId)
    .subscribe((stepData) => {
      modalContent.steps[stepIndex] = {
        ...modalContent.steps[stepIndex],
        fields: stepData,
        loading: false
      };
      modalContent.updateCurrentIndex();

      modalContent.initializeForm.addControl(key, createFormArray(stepData));

      customEventService.announceEvent(ActionTypesEnum.UPDATE_INITIALIZE_TEAM_SCORE_CARD_DATA);
    }, error => {
      modalContent.steps[stepIndex] = {
        ...modalContent.steps[stepIndex],
        loading: false,
        errorTitle: `${error.error.message.split('. ')[0]}.`,
        errorMessage: error.error.message.split('. ')[1]
      };
      modalContent.updateCurrentIndex();
      console.log('error while fetching tab detail', error);
    });
};

export const createFormArray = (fields: any[] = []): FormArray => {
  const array = new FormArray([]);

  fields.forEach(field => {
    let formValue = null;
    const value = field.data.filter(data => data.selected);

    if (value) {
      formValue = value instanceof Array ? value.map(val => val.key) : value;
    }

    const item = createFormControl(formValue, field.validators && field.validators.indexOf('required') > -1
      ? Validators['required']
      : null
    );

    array.push(item);
  });

  return array;
};

export const contributionCalculate = (data: Map<string, any>): Map<string, any> => {
  return data.updateIn(['statistics'], statistic => statistic
    .map((value, key) => {
      const calculatedPercentage = (value.get('commits') / data.get('commits')) * 100;

      return value.merge({
        percentage: `calc(${Math.floor(calculatedPercentage)}% - 2px)`,
        bgColor: colorMap.get('progress')[key],
        tooltipText: `${value.get('key')} : ${calculatedPercentage.toFixed(1)}%`
      });
    }));
};

export const projectCalculate = (data: Map<string, any>): Map<string, any> => {
  return data.updateIn(["projects"], project => project
    .map((value, key) => {
      return value.merge({
        percentage: `${value.get("value")}%`,
        bgColor: colorMap.get("progress")[key],
        tooltipText: `${value.get("key")} : ${value.get("value")}%`
      });
    }));
};

export const chartOptionCreator = (type: string, data: any): EChartOption => {
  if (!data) {
    return;
  } else {
    return commonLineChartUtil({
      grid: {
        top: "10px",
        bottom: "10px"
      },
      yAxisData: data.data,
      xAxisData: data.xAxis,
      markLineData: data.markLine,
      tooltipFormatter: type === "GIT_EFFICIENCY_AND_IMPACT_SUMMARY_SCORE_CARD" ? `{c}%<br>{b}` : `{c}<br>{b} `,
      type: type === "CODE_QUALITY_OPEN_ISSUES_HISTORICAL_VIEW_SCORE_CARD" || type === "GIT_EFFICIENCY_AND_IMPACT_SUMMARY_SCORE_CARD" ? "success" : "fail",
      opacity: 0.3
    });
  }
};

export const impactChartOptionCreator = (type: string, data: any): EChartOption => {
  if (!data) {
    return;
  }else {
    return commonLineChartUtil({
      grid: {
        top: "10px",
        bottom: "10px"
      },
      yAxisData: data.data,
      xAxisData: data.xAxis,
      markLineData: data.markLine,
      tooltipFormatter: type === "GIT_EFFICIENCY_AND_IMPACT_SUMMARY_SCORE_CARD" ? `{c}<br>{b}` : `{c}<br>{b} `,
      type: type === "GIT_EFFICIENCY_AND_IMPACT_SUMMARY_SCORE_CARD" ? "success" : "fail",
      opacity: 0.3
    });
  }
}

export const filterListMapper = (item: string): Base.FilterObjectModel => {
  return {
    value: item,
    text: item,
    selected: false
  };
};

export const filterListMapperDefaultFalse = (item: string): Base.FilterObjectModel => {
  return {
    value: item,
    text: item,
    selected: false
  };
};

export const filterListMapperSelected = (item: string, selectedList: any): { byDefault?: boolean; text: string; value: string; selected: boolean } => {
  return {
    value: item,
    text: item,
    selected: true,
    byDefault:selectedList?.filter((item1: string) => item1 === item).length > 0
  };
};

export const filterListMapperBooleanSelected = (item: string, selectedList: any): { byDefault?: boolean; text: string; value: string; selected: boolean } => {
  return {
    value: item,
    text: item === 'Yes' ? 'Active' : 'Inactive',
    selected: true,
    byDefault: selectedList?.some((item1: any) =>
      (item === 'Yes' && item1 === true) || (item === 'No' && item1 === false))
  };
};

export const filterListMapperBooleanSelectedAnalysis = (item: string, selectedList: string[]): { byDefault: boolean; text: string; value: string; selected: boolean } => {
  const isSelected = selectedList.includes(item);
  return {
    value: item,
    text: item,
    selected: isSelected,
    byDefault: isSelected,
  };
};

export const filterListDefaultMapper = (item: string): {
  byDefault: boolean;
  text: string;
  value: string;
  selected: boolean
} => {
  return {
    value: item,
    text: item,
    selected: true,
    byDefault: true
  };
};
// for depended header filters
export const filterListWithValue = (item: { label: string, key: string }): Base.FilterObjectModel => {
  return {
    value: item?.key,
    text: item?.label,
    selected: true
  };
};
